import { color } from '@stockbit/ui';
import { ChartData, ChartOptions } from 'chart.js';
import 'chartjs-adapter-date-fns';
import Image from 'global/Image';
import { CompanyChartData } from 'lib/entities/company.types';
import moment from 'moment';
import { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { EmptyChartContainer, EmptyChartText } from '../../styled';

interface StockChartProps {
  chart: CompanyChartData;
}

function StockChart({ chart }: StockChartProps) {
  const { prices = [] } = chart ?? {};

  const fillColor = color.clearDay;
  const lineColor = color.mountainMeadow;

  const data: ChartData<'line'> = {
    labels: prices.map((price) => price.formatted_date && price.date !== '0'
        ? moment(price.formatted_date).format('HH:mm')
        : ''),
    datasets: [
      {
        fill: true,
        tension: 0,
        backgroundColor: fillColor,
        borderColor: lineColor,
        borderCapStyle: 'butt',
        borderDashOffset: 0.0,
        borderJoinStyle: 'miter',
        borderWidth: 1.75,
        data: prices.map((pr) => parseInt(pr.value, 10)),
      },
    ],
  };

  const options: ChartOptions<'line'> = {
    layout: {
      padding: {
        top: 40,
        right: 10,
        left: 10,
        bottom: 10,
      },
    },
    animation: {
      duration: 0,
    },
    resizeDelay: 0, // animation duration after a resize
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    scales: {
      y: {
        position: 'right',
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          maxTicksLimit: 5,
          callback(val) {
            let price = val;
            // if price contain decimal, then hide tick
            if (typeof price === 'number' && price % 1 !== 0) price = null;
            return price || null;
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          align: 'start',
          maxTicksLimit: 5,
          maxRotation: 0,
          callback(val) {
            // eslint-disable-next-line react/no-this-in-sfc
            return this.getLabelForValue(val) || null;
          },
        },
      },
    },
    elements: {
      point: {
        radius: 0,
      },
    },
  };

  if (chart === null) {
    return (
      <EmptyChartContainer>
        <Image src="/illustration/chart-unavailable.svg" alt="Empty Chart" />
        <EmptyChartText>Data Chart Belum Tersedia</EmptyChartText>
      </EmptyChartContainer>
    );
  }

  return <Line data={data} options={options} />;
}

export default StockChart;
